import axios from 'axios';
import { FLOPFLIP_STATE_SLICE } from '@flopflip/react-redux';
import tokenAuthorization from '../interceptors/tokenAuthorization';
import idHeaders from '../interceptors/idHeaders';
import csrfHeaders from '../interceptors/csrfHeaders';
import invalidCsrfTokenHandler from '../interceptors/invalidCsrfTokenHandler';
import rewriteToProxy from '../interceptors/rewriteToProxy';
import rewriteToMock from '../interceptors/rewriteToMock';
import resourceConflict from '../interceptors/resourceConflict';
import flagNames from '../../store/featureFlags';
import { updateFlopFlipFeatureFlags } from 'store/helpers/featureFlagHelper';
import retryCallIfNeeded from '../../restCalls';
import { checkSkipMaintenancePage } from '../../store/helpers/commonHelper';
import store from '../../store/configureStore'; 

// eslint-disable-next-line no-underscore-dangle
const { REACT_APP_BYPASS_PROXY } = process.env;

export const baseClient = axios.create({});
const MAX_NETWORK_RETRY_ATTEMPTS = 5;

export const proxyClient = () => {
  const client = axios.create({});
  client.interceptors.request.use(idHeaders);
  client.interceptors.request.use(config => tokenAuthorization(config, store.getState));
  client.interceptors.request.use(config => csrfHeaders(config, store.getState));
  if (REACT_APP_BYPASS_PROXY === 'true') {
    client.interceptors.request.use(rewriteToMock);
  } else {
    client.interceptors.request.use(rewriteToProxy);
  }
  client.interceptors.response.use(
    response => response,
    error => invalidCsrfTokenHandler(error, store.dispatch, client),
  );
  client.interceptors.response.use(
    response => {
      const maintenanceFlagFromHeader = response.headers['x-maintenance-enabled'];
      const maintenanceEnabled = String(maintenanceFlagFromHeader).toLowerCase() === 'true';
      if (maintenanceEnabled) {
        const skipMaintenance = checkSkipMaintenancePage();
        if (skipMaintenance) {
          return response;
        }
        sessionStorage.setItem('nae-maintenance-enabled', 'true');
      } else {
        sessionStorage.removeItem('nae-maintenance-enabled');
        sessionStorage.removeItem('skipMaintenance');
      }
      const maintenanceFlagFromState = store.getState()[
        FLOPFLIP_STATE_SLICE
      ].flags[flagNames.MAINTENANCE_PAGE_ENABLED];
      if (maintenanceEnabled !== maintenanceFlagFromState) {
        updateFlopFlipFeatureFlags({
          MAINTENANCE_PAGE_ENABLED: maintenanceEnabled ? 'true' : 'false',
        });
      }
      return response;
    },
    error => resourceConflict(error, client),
  );
  client.interceptors.response.use(
    response => response,
    async error => {
      if (error.config?.skipNetworkRetry || error.config?.boundedRetry) {
        return Promise.reject(error);
      }
      const networkRetryAttempts = error.config?.__networkRetryAttempts || 0;
      if (error.message.includes('Network Error')
        && error.config
        && networkRetryAttempts < MAX_NETWORK_RETRY_ATTEMPTS) {
        const isCalculatorInputCreate = error.config.method === 'post'
          && error.config.url?.endsWith('/api/financialplan/v1/calculatorinput');
        const config = {
          ...error.config,
          ...(isCalculatorInputCreate
            ? { boundedRetry: true } : { __networkRetryAttempts: networkRetryAttempts + 1 }),
        };
        return retryCallIfNeeded(client, () => client.request(config));
      }
      return Promise.reject(error);
    },
  );

  return client;
};

export const proxyClientInstance = proxyClient();
